import { ContentType, PopupShare } from '@components/learning';
import { Button, ButtonAppearance } from '@components/shared/Button';
import { Icon } from '@components/shared/Icon';
import { InformationCardState } from '@components/learning/InformationCard';
import { css } from '@emotion/react';
import { token } from '@atlaskit/tokens';
import { useDeviceSize } from '@hooks/useDeviceSize';

export interface LearningControlPanelProps {
  /** Defines the states of the card to show or not different buttons */
  state?: InformationCardState;
  /** Indicates when star icon should be filled */
  isSaved: boolean;
  /** Function to be executed on save button */
  onSave: () => void;
  /** Url of the course to be shared */
  urlToShare: string;
  /** Type of content (Course or Learning Path) */
  contentType: ContentType;
  /** Function to be executed on click button */
  onClick: () => void;
}

export const LearningControlPanel = ({
  state = InformationCardState.DEFAULT,
  isSaved,
  onSave,
  urlToShare,
  contentType,
  onClick,
}: LearningControlPanelProps): React.ReactElement => {
  const isCourse = contentType === ContentType.COURSE;
  const buttonLabel = isCourse ? 'course' : 'learning path';

  const width = useDeviceSize();
  const TABLET_WIDTH = 920;

  const handleOnClick = (event?: React.MouseEvent): void => {
    if (onClick) {
      event?.preventDefault();
      onClick();
    }
  };

  const ButtonStateProps = {
    [InformationCardState.LOADING]: {
      label: '',
      isLoading: true,
      fullWidth: true,
    },
    [InformationCardState.DEFAULT]: {
      label: `Start ${buttonLabel}`,
      appearance: ButtonAppearance.PRIMARY,
      fullWidth: true,
    },
    [InformationCardState.IN_PROGRESS]: {
      label: `Continue ${buttonLabel}`,
      appearance: ButtonAppearance.PRIMARY,
      fullWidth: true,
    },
    [InformationCardState.FINISHED]: {
      label: `Review ${buttonLabel}`,
      appearance: ButtonAppearance.SECONDARY,
      fullWidth: true,
    },
  };

  const handleOnSave = () => {
    if (onSave) {
      onSave();
    }
  };

  return (
    <div data-testid="control-panel-card" css={learningControlPanelStyles}>
      <div css={iconButtonsContainerStyles}>
        <Button
          iconColor={token('color.icon.subtle')}
          label=""
          appearance={ButtonAppearance.ICON}
          iconBefore={<Icon type={isSaved ? 'bookmark-filled' : 'bookmark'} />}
          onClick={handleOnSave}
        />
        <PopupShare urlToShare={urlToShare} alignToTop={width <= TABLET_WIDTH} fullWidth={true} />
      </div>
      <div data-testid="control-panel-cta-button" css={statusButtonContainerStyles}>
        {state === InformationCardState.LOADING && <Button {...ButtonStateProps[InformationCardState.LOADING]} />}
        {state === InformationCardState.DEFAULT && (
          <Button onClick={handleOnClick} {...ButtonStateProps[InformationCardState.DEFAULT]} />
        )}
        {state === InformationCardState.IN_PROGRESS && (
          <Button onClick={handleOnClick} {...ButtonStateProps[InformationCardState.IN_PROGRESS]} />
        )}
        {state === InformationCardState.FINISHED && (
          <Button onClick={handleOnClick} {...ButtonStateProps[InformationCardState.FINISHED]} />
        )}
      </div>
    </div>
  );
};

const learningControlPanelStyles = css({
  position: 'relative',
  padding: token('space.200'),
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  border: `1px solid ${token('color.border')}`,
  borderRadius: '8px',
  borderTopRightRadius: 0,
  borderTopLeftRadius: 0,
  backgroundColor: token('color.background.input'),
  zIndex: 2,
  borderLeft: 'none',
  borderRight: 'none',

  '@media (max-width: 920px)': {
    borderBottom: 'none',
  },
});

const iconButtonsContainerStyles = css({
  display: 'flex',
  justifyContent: 'space-between',
  gap: token('space.150'),
  marginRight: token('space.150'),

  '[id*="icon-button"]': {
    width: '38px',
    height: '38px',
    backgroundColor: token('color.background.neutral'),

    ':hover': {
      backgroundColor: token('color.background.neutral.hovered'),
    },
  },
});

const statusButtonContainerStyles = css({
  flex: 1,
  width: '100%',
  a: {
    padding: `${token('space.100')} ${token('space.150')}`,
  },
  button: {
    width: '100%',
    height: '38px',
  },
});
